import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import JsonEditor from 'components/editors/JsonEditor';
import { type FC, useState } from 'react';
import type { FollowUpConfigDraft } from 'services/types/followUpConfig';
import { INSUREX_BUSINESS_UNIT_ID } from 'utils/constants';

interface ConditionsAndComponentsSectionProps {
  followUpConfigDraft: FollowUpConfigDraft;
  isEditing: boolean;
  setFollowUpConfigDraft?: (followUpConfigDraft: FollowUpConfigDraft) => void;
}

const ConditionsAndComponentsSection: FC<ConditionsAndComponentsSectionProps> = ({
  followUpConfigDraft,
  isEditing,
  setFollowUpConfigDraft,
}) => {
  const [conditionsError, setConditionsError] = useState(false);
  const [templateError, setTemplateError] = useState(false);

  return (
    <Card sx={{ mb: 1.5, boxShadow: 0, borderRadius: 0 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
          Condiciones y Componentes
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: followUpConfigDraft.templateName !== null ? 6 : 12 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
              Condiciones para enviar:
            </Typography>
            {isEditing ? (
              <>
                <JsonEditor
                  value={followUpConfigDraft.conditionsToSend ?? ''}
                  onChange={(value) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, conditionsToSend: value })}
                  onValidityChange={(valid) => setConditionsError(!valid)}
                  error={conditionsError}
                  minHeight={120}
                  maxHeight={400}
                  ariaLabel="Condiciones para enviar (JSON)"
                />
                {conditionsError && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    JSON inválido
                  </Typography>
                )}
                {followUpConfigDraft.businessUnitId === INSUREX_BUSINESS_UNIT_ID && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Los filtros de venta (<i>sale</i>) pueden ser pesados para la base de datos. Úsalos solo si es
                    necesario y mantén una ventana acotada entre el tiempo mínimo y máximo de espera.
                  </Alert>
                )}
              </>
            ) : (
              <JsonEditor
                value={followUpConfigDraft.conditionsToSend ?? ''}
                onChange={() => undefined}
                readOnly
                maxHeight={300}
                ariaLabel="Condiciones para enviar (JSON)"
              />
            )}
          </Grid>
          {followUpConfigDraft.templateName !== null && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                Componentes de plantilla:
              </Typography>
              {isEditing ? (
                <>
                  <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                    Pueden ser atributos del cliente por ejemplo: <i>timeOf.client.visitDate</i> o{' '}
                    <i>client.branchOffice</i>
                    {followUpConfigDraft.businessUnitId === INSUREX_BUSINESS_UNIT_ID && (
                      <>
                        , o datos de la venta del último lead por ejemplo: <i>sale.loanData.pendingBalance</i> o{' '}
                        <i>sale.loanProtectionInsurance.insuranceData.refundAmount</i>
                      </>
                    )}
                  </Typography>
                  <JsonEditor
                    value={followUpConfigDraft.templateComponents ?? ''}
                    onChange={(value) =>
                      setFollowUpConfigDraft?.({ ...followUpConfigDraft, templateComponents: value })
                    }
                    onValidityChange={(valid) => setTemplateError(!valid)}
                    error={templateError}
                    minHeight={120}
                    maxHeight={400}
                    ariaLabel="Componentes de plantilla (JSON)"
                  />
                  {templateError && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      JSON inválido
                    </Typography>
                  )}
                </>
              ) : (
                <JsonEditor
                  value={followUpConfigDraft.templateComponents ?? ''}
                  onChange={() => undefined}
                  readOnly
                  maxHeight={300}
                  ariaLabel="Componentes de plantilla (JSON)"
                />
              )}
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default ConditionsAndComponentsSection;
